import { createContext, useContext } from 'react';
import { Language } from '../common/verbiage';

export type PatientClinicLocation = {
  ID: number;
  path: string | null;
  name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  primary: boolean;
  timezone: string | null;
};

export type PatientClinic = {
  clinicId: number;
  clinicName: string;
  path?: string;
  primaryColor?: string;
  accentColor?: string;
  logo?: string;
  phone?: string;
  email?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  site?: string;
  facebook?: string;
  twitter?: string;
  youtube?: string;
  // All active locations for the clinic. Used by the Schedule flow to
  // surface a location-picker step when the clinic operates from more
  // than one location.
  availableLocations?: PatientClinicLocation[];
  // EHR-vs-essentials flag from the Clinic row. Essentials clinics
  // (`ehr: false`) don't have appointments / balances / patient-side
  // booking, so the portal hides those surfaces for them.
  ehr?: boolean;
  // Clinic-level "Allow patients to pay online through HealthCom"
  // toggle. When false, the Billing tab still renders read-only
  // balance info but Pay / Add-card actions are blocked. Defaults
  // to true when the clinic hasn't set it.
  allowOnlinePayments?: boolean;
};

export type PatientInfo = {
  ID: string;
  fname: string;
  mname?: string;
  lname: string;
  email?: string;
  username?: string;
  language: Language;
  exercises: any[];
  conditions: any[];
  advice: any[];
  fileAttachments?: { name: string; url: string }[];
  balances?: {
    balance: number;
    creditBalance: number;
    payorBalance: number;
    patientBalance: number;
    unallocatedBalance: number;
  };
  unsubscribe?: 0 | 1;
  // Pain-level + exercise-completion log, keyed by ISO timestamp.
  // The home + exercise CTA gates "once per day" off this; backend
  // also rejects same-UTC-day duplicates as a safety net.
  completedSurveys?: { [iso: string]: { painLevel: number } };
  primaryClinician?: string;
  clinic: PatientClinic & {
    name?: string;
    doNotAllowUnrelatedRegions?: boolean;
    flags?: string[];
  };
};

export type PatientPortalState = {
  /** All clinics the user has accounts at */
  clinics: PatientClinic[];
  /** The selected clinic (null if not yet chosen) */
  selectedClinic: PatientClinic | null;
  /** All patients at the selected clinic sharing this login credential */
  patients: PatientInfo[];
  /** The selected patient (null if not yet chosen) */
  selectedPatient: PatientInfo | null;
  /** Whether patient data is loading */
  isLoading: boolean;
  /** Current language */
  language: Language;
  /** Select a clinic */
  selectClinic: (clinic: PatientClinic) => void;
  /** Select a patient */
  selectPatient: (patient: PatientInfo) => void;
  /** Go back to clinic selection */
  resetClinicSelection: () => void;
  /** Go back to patient selection */
  resetPatientSelection: () => void;
  /** Refresh patient data from API */
  refreshPatientData: () => Promise<void>;
};

const defaultState: PatientPortalState = {
  clinics: [],
  selectedClinic: null,
  patients: [],
  selectedPatient: null,
  isLoading: false,
  language: 'en',
  selectClinic: () => {},
  selectPatient: () => {},
  resetClinicSelection: () => {},
  resetPatientSelection: () => {},
  refreshPatientData: async () => {},
};

export const PatientPortalContext = createContext<PatientPortalState>(defaultState);

export const usePatientPortal = () => useContext(PatientPortalContext);
